import React, { useEffect, useMemo, useState } from 'react';
import type { AdminTenantOption } from '../components/AdminTenantSelector';
import { LoadingIndicator, Notice } from '../components/status';
import { PermissionsPageHero } from '../components/permissions/PermissionsPageHero';
import { PermissionsTabs } from '../components/permissions/PermissionsTabs';
import { StateTab } from '../components/permissions/StateTab';
import { PolicyTab } from '../components/permissions/PolicyTab';
import { ApplyTab } from '../components/permissions/ApplyTab';
import { DriftTab } from '../components/permissions/DriftTab';
// PlaceholderTab no longer used now that all four tabs have real content.
import { usePortalContext } from '../context/PortalContext';
import type {
  AssignmentRule,
  BaselineAdmin,
  BaselineAggregateDrift,
  BaselineAggregateState,
  EnterpriseBaselinePolicy,
  OverallState,
  PermissionsManagementMode,
  PermissionsTab,
  PostureResponse
} from '../components/permissions/types';

interface PolicyVersionSummary {
  versionId: string;
  versionNumber: number;
  publishedAt: string;
  publishedBy?: string;
  notes?: string;
}

const computeOverallState = (posture: PostureResponse): OverallState => {
  const missing = posture.summary.missingCriticalGroupInstances;
  if (missing > 0) return 'at-risk';
  const stale = posture.summary.staleInstances;
  if (stale > 0) return 'warning';
  if (posture.summary.inspectableInstances < posture.summary.totalInstances) return 'warning';
  return 'healthy';
};

const Permissions = (): JSX.Element => {
  const { tenant } = usePortalContext();
  const canManagePermissions = tenant?.capabilities?.canManagePermissions === true;
  const [deployableTenants, setDeployableTenants] = useState<AdminTenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [posture, setPosture] = useState<PostureResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PermissionsTab>('state');
  const [applyFocus, setApplyFocus] = useState<{ instanceId: string } | null>(null);
  const [exporting, setExporting] = useState<'csv' | 'json' | null>(null);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyVersions, setPolicyVersions] = useState<PolicyVersionSummary[]>([]);
  const [publishingPolicy, setPublishingPolicy] = useState(false);
  // Enterprise baseline — now backed by real subscription-scoped storage.
  // The picker entry in the hero is enabled when the user covers more than one tenant.
  const [baselineLayerInFocus, setBaselineLayerInFocus] = useState(false);
  const [baselinePolicy, setBaselinePolicy] = useState<EnterpriseBaselinePolicy | null>(null);
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [baselineError, setBaselineError] = useState<string | null>(null);
  const [baselineSaving, setBaselineSaving] = useState(false);
  const [baselineAggregateState, setBaselineAggregateState] = useState<BaselineAggregateState | null>(null);
  const [baselineAggregateDrift, setBaselineAggregateDrift] = useState<BaselineAggregateDrift | null>(null);

  const loadBaseline = async (): Promise<void> => {
    if (!canManagePermissions) return;
    setBaselineLoading(true);
    setBaselineError(null);
    try {
      const response = await fetch('/api/permissions/baseline');
      if (response.status === 403) {
        // Not a customer admin for a subscription — keep baseline empty.
        setBaselinePolicy(null);
        return;
      }
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Failed to load baseline (${response.status})`);
      }
      const body = (await response.json()) as {
        baseline: { subscriptionId: string; baselineSystemAdmins: BaselineAdmin[]; assignmentRules: AssignmentRule[]; updatedAt?: string; updatedBy?: string };
      };
      setBaselinePolicy({
        subscriptionId: body.baseline.subscriptionId,
        baselineAdmins: body.baseline.baselineSystemAdmins ?? [],
        assignmentRules: body.baseline.assignmentRules ?? [],
        updatedAt: body.baseline.updatedAt,
        updatedBy: body.baseline.updatedBy
      });
    } catch (err) {
      setBaselineError((err as Error).message);
    } finally {
      setBaselineLoading(false);
    }
  };

  const applyBaselineResponse = (baseline: {
    subscriptionId: string;
    baselineSystemAdmins?: BaselineAdmin[];
    assignmentRules?: AssignmentRule[];
    updatedAt?: string;
    updatedBy?: string;
  }): void => {
    setBaselinePolicy({
      subscriptionId: baseline.subscriptionId,
      baselineAdmins: baseline.baselineSystemAdmins ?? [],
      assignmentRules: baseline.assignmentRules ?? [],
      updatedAt: baseline.updatedAt,
      updatedBy: baseline.updatedBy
    });
  };

  const callBaselineEndpoint = async (
    path: string,
    init: RequestInit,
    failureCode: string
  ): Promise<void> => {
    setBaselineSaving(true);
    setBaselineError(null);
    try {
      const response = await fetch(path, init);
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `${failureCode} (${response.status})`);
      }
      const body = (await response.json()) as { baseline?: Parameters<typeof applyBaselineResponse>[0] };
      if (body.baseline) {
        applyBaselineResponse(body.baseline);
      } else {
        await loadBaseline();
      }
    } catch (err) {
      setBaselineError((err as Error).message);
    } finally {
      setBaselineSaving(false);
    }
  };

  const addBaselineLayerAdmin = (admin: BaselineAdmin): Promise<void> => callBaselineEndpoint(
    '/api/permissions/baseline/system-admins',
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: admin.email, displayName: admin.displayName })
    },
    'Failed to add baseline admin'
  );

  const removeBaselineLayerAdmin = (email: string): Promise<void> => callBaselineEndpoint(
    `/api/permissions/baseline/system-admins/${encodeURIComponent(email)}`,
    { method: 'DELETE' },
    'Failed to remove baseline admin'
  );

  const addBaselineLayerAssignmentRule = (
    rule: Omit<AssignmentRule, 'ruleId' | 'isActive'>
  ): Promise<void> => callBaselineEndpoint(
    '/api/permissions/baseline/assignments',
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        groupKey: rule.groupKey,
        principalKind: rule.principalKind,
        principalIdentifier: rule.principalIdentifier,
        principalEmail: rule.principalEmail,
        principalDisplayName: rule.principalDisplayName,
        targetMode: rule.targetMode,
        targetInstanceIds: rule.targetInstanceIds
      })
    },
    'Failed to add baseline assignment rule'
  );

  const removeBaselineLayerAssignmentRule = (ruleId: string): Promise<void> => callBaselineEndpoint(
    `/api/permissions/baseline/assignments/${encodeURIComponent(ruleId)}`,
    { method: 'DELETE' },
    'Failed to remove baseline assignment rule'
  );

  const loadBaselineAggregates = async (): Promise<void> => {
    if (!baselineLayerInFocus || !canManagePermissions) return;
    try {
      const [stateResp, driftResp] = await Promise.all([
        fetch('/api/permissions/baseline/aggregate/state'),
        fetch('/api/permissions/baseline/aggregate/drift')
      ]);
      if (stateResp.ok) {
        setBaselineAggregateState(await stateResp.json() as BaselineAggregateState);
      }
      if (driftResp.ok) {
        setBaselineAggregateDrift(await driftResp.json() as BaselineAggregateDrift);
      }
    } catch (err) {
      setBaselineError((err as Error).message);
    }
  };

  const loadTenants = async (): Promise<void> => {
    if (!canManagePermissions) {
      setLoading(false);
      return;
    }
    try {
      const response = await fetch('/api/admin/spfx-releases/deployable-tenants');
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Failed to load tenants (${response.status})`);
      }
      const payload = (await response.json()) as {
        tenants?: AdminTenantOption[];
        defaultTenantId?: string;
      };
      const tenants = Array.isArray(payload.tenants) ? payload.tenants : [];
      setDeployableTenants(tenants);
      setSelectedTenantId((current) => current || payload.defaultTenantId || tenants[0]?.tenantId || '');
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const load = async (): Promise<void> => {
    if (!selectedTenantId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/permissions/posture?${params.toString()}`);
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Failed to load posture (${response.status})`);
      }
      const body = await response.json() as PostureResponse;
      setPosture(body);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const loadVersions = async (): Promise<void> => {
    if (!selectedTenantId) {
      setPolicyVersions([]);
      return;
    }
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(`/api/permissions/policy/versions?${params.toString()}`);
      if (!response.ok) {
        // Treat "not yet available" silently — the table may not exist on this deployment.
        setPolicyVersions([]);
        return;
      }
      const body = (await response.json().catch(() => ({}))) as { versions?: PolicyVersionSummary[] };
      setPolicyVersions(Array.isArray(body.versions) ? body.versions : []);
    } catch {
      setPolicyVersions([]);
    }
  };

  const publishPolicy = async (notes?: string): Promise<void> => {
    if (!selectedTenantId) return;
    setPublishingPolicy(true);
    setError(null);
    try {
      const response = await fetch('/api/permissions/policy/publish', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ tenantId: selectedTenantId, notes })
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Failed to publish policy (${response.status})`);
      }
      await loadVersions();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPublishingPolicy(false);
    }
  };

  const exportPosture = async (format: 'csv' | 'json'): Promise<void> => {
    setExporting(format);
    try {
      const params = new URLSearchParams({ format, tenantId: selectedTenantId });
      const response = await fetch(`/api/permissions/posture/export?${params.toString()}`);
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Export failed (${response.status})`);
      }
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const nameMatch = /filename="([^"]+)"/i.exec(disposition);
      const fallback = `permissions-posture.${format}`;
      const fileName = nameMatch?.[1] || fallback;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setExporting(null);
    }
  };

  const saveGovernance = async (
    input: { managementMode: PermissionsManagementMode; enforceSystemAdmins: boolean }
  ): Promise<void> => {
    setSavingPolicy(true);
    setError(null);
    try {
      const response = await fetch('/api/permissions/policy', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          managementMode: input.managementMode,
          enforceSystemAdmins: input.enforceSystemAdmins
        })
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Failed to update policy (${response.status})`);
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingPolicy(false);
    }
  };

  const addBaselineAdmin = async (admin: BaselineAdmin): Promise<void> => {
    setSavingPolicy(true);
    setError(null);
    try {
      const response = await fetch('/api/permissions/policy/system-admins', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          email: admin.email,
          displayName: admin.displayName
        })
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Failed to add baseline admin (${response.status})`);
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingPolicy(false);
    }
  };

  const removeBaselineAdmin = async (email: string): Promise<void> => {
    setSavingPolicy(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(
        `/api/permissions/policy/system-admins/${encodeURIComponent(email)}?${params.toString()}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Failed to remove baseline admin (${response.status})`);
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingPolicy(false);
    }
  };

  const addAssignmentRule = async (
    rule: Omit<AssignmentRule, 'ruleId' | 'isActive'>
  ): Promise<void> => {
    setSavingPolicy(true);
    setError(null);
    try {
      const response = await fetch('/api/permissions/policy/assignments', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          tenantId: selectedTenantId,
          groupKey: rule.groupKey,
          principalKind: rule.principalKind,
          principalIdentifier: rule.principalIdentifier,
          principalEmail: rule.principalEmail,
          principalDisplayName: rule.principalDisplayName,
          targetMode: rule.targetMode,
          targetInstanceIds: rule.targetInstanceIds
        })
      });
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Failed to add assignment rule (${response.status})`);
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingPolicy(false);
    }
  };

  const removeAssignmentRule = async (ruleId: string): Promise<void> => {
    setSavingPolicy(true);
    setError(null);
    try {
      const params = new URLSearchParams({ tenantId: selectedTenantId });
      const response = await fetch(
        `/api/permissions/policy/assignments/${encodeURIComponent(ruleId)}?${params.toString()}`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { message?: string };
        throw new Error(payload.message ?? `Failed to remove assignment rule (${response.status})`);
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingPolicy(false);
    }
  };

  useEffect(() => {
    void loadTenants();
  }, [canManagePermissions]);

  useEffect(() => {
    if (!canManagePermissions) {
      return;
    }
    void load();
    void loadVersions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManagePermissions, selectedTenantId]);

  useEffect(() => {
    if (!canManagePermissions) return;
    void loadBaseline();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManagePermissions]);

  useEffect(() => {
    if (!baselineLayerInFocus) return;
    void loadBaselineAggregates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineLayerInFocus, activeTab]);

  const overallState: OverallState = useMemo(() => {
    if (!posture) return 'warning';
    return computeOverallState(posture);
  }, [posture]);

  if (!canManagePermissions) {
    return (
      <div className="card">
        <h1 className="section-title">Permissions</h1>
        <p className="muted">
          You do not have permission-management access for this tenant. Ask a customer system admin
          to add you, or use an Edinshall support role where appropriate.
        </p>
      </div>
    );
  }

  return (
    <div className="permissions-page">
      <PermissionsPageHero
        tenants={deployableTenants}
        selectedTenantId={selectedTenantId}
        onSelectTenant={(id) => {
          setBaselineLayerInFocus(false);
          setSelectedTenantId(id);
        }}
        policy={posture?.policy ?? null}
        summary={posture?.summary ?? null}
        generatedAtUtc={posture?.generatedAt ?? null}
        overallState={overallState}
        enterpriseBaselineAvailable={deployableTenants.length > 1}
        baselineLayerInFocus={baselineLayerInFocus}
        onSelectBaselineLayer={() => setBaselineLayerInFocus(true)}
      />

      <PermissionsTabs
        active={activeTab}
        onChange={(tab) => {
          // Drift's "Fix in Apply" focus only survives its own transition;
          // navigating via the tab bar starts Apply unscoped as usual.
          setApplyFocus(null);
          setActiveTab(tab);
        }}
      />

      {loading && <LoadingIndicator label="Loading permission posture…" />}
      {error && !loading && <Notice kind="warning">{error}</Notice>}

      {!loading && !error && posture && !baselineLayerInFocus && (
        <div
          role="tabpanel"
          id={`permissions-panel-${activeTab}`}
          aria-labelledby={`permissions-tab-${activeTab}`}
        >
          {activeTab === 'state' && (
            <StateTab posture={posture} exporting={exporting} onExport={(fmt) => void exportPosture(fmt)} />
          )}
          {activeTab === 'policy' && (
            <PolicyTab
              policy={posture.policy}
              instances={posture.instances}
              saving={savingPolicy}
              onSaveGovernance={(input) => void saveGovernance(input)}
              onAddBaselineAdmin={(admin) => void addBaselineAdmin(admin)}
              onRemoveBaselineAdmin={(email) => void removeBaselineAdmin(email)}
              onAddAssignmentRule={(rule) => void addAssignmentRule(rule)}
              onRemoveAssignmentRule={(ruleId) => void removeAssignmentRule(ruleId)}
              inheritedBaseline={deployableTenants.length > 1 ? baselinePolicy : null}
              versions={policyVersions}
              publishing={publishingPolicy}
              onPublish={(notes) => void publishPolicy(notes)}
            />
          )}
          {activeTab === 'apply' && (
            <ApplyTab
              tenantId={selectedTenantId}
              policy={posture.policy}
              instances={posture.instances}
              focus={applyFocus}
            />
          )}
          {activeTab === 'drift' && (
            <DriftTab
              tenantId={selectedTenantId}
              instances={posture.instances}
              onSwitchToApply={(instanceId) => {
                // Fresh object identity so re-clicking the same record
                // re-applies the scoped target in ApplyTab.
                setApplyFocus({ instanceId });
                setActiveTab('apply');
              }}
            />
          )}
        </div>
      )}

      {!loading && !error && baselineLayerInFocus && (
        <div
          role="tabpanel"
          id={`permissions-panel-${activeTab}`}
          aria-labelledby={`permissions-tab-${activeTab}`}
        >
          {baselineError && <Notice kind="warning">{baselineError}</Notice>}
          {baselineLoading && !baselinePolicy && <LoadingIndicator label="Loading Enterprise baseline…" />}
          {!baselinePolicy && !baselineLoading && (
            <div className="permissions-placeholder">
              <h3>No Enterprise baseline available</h3>
              <p className="muted">
                The Enterprise baseline is for subscription customer admins. Switch back to a specific
                tenant to use the per-tenant Permissions tabs.
              </p>
            </div>
          )}

          {baselinePolicy && activeTab === 'policy' && (
            <PolicyTab
              policy={{
                tenantId: '__enterprise__',
                managementMode: 'portal',
                enforceSystemAdmins: false,
                baselineSystemAdmins: baselinePolicy.baselineAdmins,
                assignmentRules: baselinePolicy.assignmentRules,
                updatedAt: baselinePolicy.updatedAt,
                updatedBy: baselinePolicy.updatedBy
              }}
              instances={posture?.instances ?? []}
              saving={baselineSaving}
              onSaveGovernance={() => undefined /* not applicable at baseline */}
              onAddBaselineAdmin={(admin) => void addBaselineLayerAdmin(admin)}
              onRemoveBaselineAdmin={(email) => void removeBaselineLayerAdmin(email)}
              onAddAssignmentRule={(rule) => void addBaselineLayerAssignmentRule(rule)}
              onRemoveAssignmentRule={(ruleId) => void removeBaselineLayerAssignmentRule(ruleId)}
              hideGovernance
              bannerText={`Editing the Enterprise baseline. Saves persist to the subscription baseline; the projection step that pushes these onto each covered tenant lands with Apply.${baselinePolicy.updatedAt ? ` Last updated ${new Date(baselinePolicy.updatedAt).toLocaleString()}${baselinePolicy.updatedBy ? ` by ${baselinePolicy.updatedBy}` : ''}.` : ''}`}
            />
          )}

          {baselinePolicy && activeTab === 'state' && (
            <div className="card">
              <h3>State across covered tenants</h3>
              <p className="muted">Per-tenant posture summary for the {baselineAggregateState?.coveredTenantIds.length ?? '—'} tenant(s) this subscription covers.</p>
              {!baselineAggregateState && <LoadingIndicator label="Loading aggregate state…" />}
              {baselineAggregateState && (
                <table className="baseline-aggregate-table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Total instances</th>
                      <th>Healthy</th>
                      <th>Stale</th>
                      <th>Missing critical groups</th>
                      <th>Policy last saved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {baselineAggregateState.tenants.map((entry) => (
                      <tr key={entry.tenantId}>
                        <td className="mono">{entry.tenantId}</td>
                        {entry.error ? (
                          <td colSpan={5} className="muted">Error: {entry.error}</td>
                        ) : (
                          <>
                            <td>{entry.summary?.totalInstances ?? 0}</td>
                            <td>{entry.summary?.healthyInstances ?? 0}</td>
                            <td>{entry.summary?.staleInstances ?? 0}</td>
                            <td>{entry.summary?.missingCriticalGroupInstances ?? 0}</td>
                            <td className="muted">{entry.policyUpdatedAt ? new Date(entry.policyUpdatedAt).toLocaleString() : '—'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {baselinePolicy && activeTab === 'apply' && (
            <div className="permissions-placeholder">
              <h3>Apply baseline to covered tenants</h3>
              <p className="muted">
                Per-tenant Apply is live — switch back to a specific tenant in the picker to run
                preflight / preview / apply for that tenant. Subscription-wide fan-out (one click
                to apply the baseline across every covered tenant) is the next follow-up; the
                preview will be grouped by tenant.
              </p>
            </div>
          )}

          {baselinePolicy && activeTab === 'drift' && (
            <div className="card">
              <h3>Drift across covered tenants</h3>
              <p className="muted">Per-tenant drift summary (counts only — open the per-tenant Drift tab for detail).</p>
              {!baselineAggregateDrift && <LoadingIndicator label="Loading aggregate drift…" />}
              {baselineAggregateDrift && (
                <table className="baseline-aggregate-table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Open drift</th>
                      <th>Last scan</th>
                      <th>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {baselineAggregateDrift.tenants.map((entry) => (
                      <tr key={entry.tenantId}>
                        <td className="mono">{entry.tenantId}</td>
                        {entry.error ? (
                          <td colSpan={3} className="muted">Error: {entry.error}</td>
                        ) : (
                          <>
                            <td>{entry.openCount ?? 0}</td>
                            <td className="muted">{entry.lastScanAt ? new Date(entry.lastScanAt).toLocaleString() : 'Never'}</td>
                            <td className="muted">{entry.lastScanBy ?? '—'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Permissions;
